import React from "react";
import { Link } from "react-router-dom";

export default function ResearchEfficiently() {
  return (
    <section className="relative bg-[#02042b] text-white mt-12">

      {/* subtle glow */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(900px 400px at 10% -10%, rgba(99,102,241,.18), transparent 60%), radial-gradient(800px 360px at 120% 0%, rgba(236,72,153,.15), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">

        {/* HEADER */}
        <header className="text-center max-w-3xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            How to Research Effectively for Your Assignment:
            <br className="hidden sm:block" /> From Google Scholar to JSTOR
          </h1>

          <p className="mt-3 text-gray-300">
            Learn how to research effectively for your assignment using Google Scholar,
            JSTOR, and expert strategies for credible, organized results.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* SIDEBAR */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white/5 rounded-xl border border-white/10 p-5 backdrop-blur">
              <h2 className="text-lg font-semibold mb-3">Table of Contents</h2>

              <nav className="text-sm text-gray-300 space-y-2">
                <a href="#intro" className="block hover:text-white">Introduction</a>
                <a href="#keypoints" className="block hover:text-white">Key Points</a>
                <a href="#requirements" className="block hover:text-white">Understand Requirements</a>
                <a href="#databases" className="block hover:text-white">Use Academic Databases</a>
                <a href="#credibility" className="block hover:text-white">Evaluate Sources</a>
                <a href="#organize" className="block hover:text-white">Organize Research</a>
                <a href="#integrate" className="block hover:text-white">Integrate Research</a>
                <a href="#strategies" className="block hover:text-white">Extra Strategies</a>
                <a href="#support" className="block hover:text-white">How We Help</a>
                <a href="#faqs" className="block hover:text-white">FAQs</a>
                <a href="#conclusion" className="block hover:text-white">Conclusion</a>
              </nav>
            </div>
          </aside>

          {/* ARTICLE */}
          <article className="lg:col-span-8 space-y-10">

            {/* INTRO */}
            <section id="intro" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <p className="text-gray-200">
                Researching effectively is a core academic skill. From finding credible
                sources to organizing information, good research saves time, reduces
                stress, and leads to stronger assignments.
              </p>

              <p className="text-gray-200 mt-3">
                In this guide, you’ll learn how to navigate Google Scholar and JSTOR,
                evaluate sources, and integrate research smoothly into your writing.
              </p>
            </section>

            {/* KEY POINTS */}
            <section id="keypoints" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Key Points</h2>

              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Understand the assignment clearly.</li>
                <li>Use academic databases like Google Scholar & JSTOR.</li>
                <li>Check credibility: author, journal, citations.</li>
                <li>Organize notes, folders, and citations properly.</li>
                <li>Integrate research through paraphrasing & analysis.</li>
              </ul>
            </section>

            {/* REQUIREMENTS */}
            <section id="requirements" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">1. Understand Your Assignment Requirements</h2>

              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Clarify the topic:</strong> narrow broad topics.</li>
                <li><strong>Know the purpose:</strong> inform, analyze, or argue.</li>
                <li><strong>Follow the format:</strong> APA, MLA, or Chicago.</li>
                <li><strong>Define scope:</strong> broad review vs focused analysis.</li>
              </ul>
            </section>

            {/* DATABASES */}
            <section id="databases" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">2. Start with Trusted Academic Databases</h2>

              <h3 className="font-semibold mt-3 text-white">Google Scholar</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Cited by:</strong> shows article impact.</li>
                <li><strong>Related articles:</strong> explore similar topics.</li>
                <li><strong>Alerts:</strong> new research updates.</li>
                <li><strong>Advanced search:</strong> use AND / OR / NOT.</li>
              </ul>

              <h3 className="font-semibold mt-5 text-white">JSTOR</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Great for humanities & social sciences.</li>
                <li>Research folders for organization.</li>
                <li>Primary source archives.</li>
                <li>Built-in citation tools.</li>
              </ul>

              <p className="text-gray-300 mt-3">
                Also read:{" "}
                <Link to="/blog/jstor-research-tips" className="text-pink-400 underline">
                  JSTOR Research Tips
                </Link>
              </p>
            </section>

            {/* CREDIBILITY */}
            <section id="credibility" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">3. Evaluate Sources for Credibility</h2>

              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Check author qualifications.</li>
                <li>Prefer peer-reviewed journals.</li>
                <li>Verify publication date.</li>
                <li>Look for citations and references.</li>
              </ul>
            </section>

            {/* ORGANIZATION */}
            <section id="organize" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">4. Organize Your Research Effectively</h2>

              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Use Zotero, Mendeley, or EndNote.</li>
                <li>Create folders by topic or section.</li>
                <li>Highlight key notes & quotes.</li>
              </ul>
            </section>

            {/* INTEGRATION */}
            <section id="integrate" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">5. Integrate Research into Your Writing</h2>

              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Paraphrase instead of copying.</li>
                <li>Use quotations sparingly.</li>
                <li>Add critical analysis, not just summary.</li>
              </ul>
            </section>

            {/* STRATEGIES */}
            <section id="strategies" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">6. Additional Research Strategies</h2>

              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Snowball research through references.</li>
                <li>Use library access whenever possible.</li>
                <li>Consider interviews or surveys when suitable.</li>
              </ul>
            </section>

            {/* HELP */}
            <section id="support" className="bg-gradient-to-r from-rose-500/20 to-indigo-500/20 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-2">How We Help</h2>
              <p className="text-gray-200">
                Infinite Solution supports students with structured research help,
                plagiarism-free writing guidance, and citation support across major disciplines.
              </p>
            </section>

            {/* FAQS */}
            <section id="faqs" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">FAQs</h2>

              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Start by understanding the assignment clearly.</li>
                <li>Check credibility using peer-review and citations.</li>
                <li>Use both Google Scholar and JSTOR together.</li>
                <li>Organize references using digital tools.</li>
              </ul>
            </section>

            {/* CONCLUSION */}
            <section id="conclusion" className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Conclusion</h2>

              <p className="text-gray-200">
                Research becomes easier when you search smart, stay organized, and evaluate sources carefully.
                With the right tools, every assignment becomes stronger, clearer, and more credible.
              </p>
            </section>

          </article>
        </div>
      </div>
    </section>
  );
}
